import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X } from "@phosphor-icons/react";
import api from "@/lib/api";

const Badge = ({ status }) => {
  const cls = { pending: "bg-amber-50 text-amber-600", approved: "bg-blue-50 text-[#007AFF]", rejected: "bg-red-50 text-[#FF3B30]" }[status] || "bg-slate-100 text-slate-500";
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-sm uppercase tracking-wider ${cls}`}>{status}</span>;
};

const Actions = ({ onDecide, id, kind }) => (
  <div className="flex gap-1 justify-end">
    <button data-testid={`approve-${kind}-${id}`} onClick={() => onDecide("approved")} className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold border border-slate-200 rounded-md text-[#007AFF] hover:bg-blue-50 transition-colors duration-150">
      <Check size={12} weight="bold" /> Approve
    </button>
    <button data-testid={`reject-${kind}-${id}`} onClick={() => onDecide("rejected")} className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold border border-slate-200 rounded-md text-[#FF3B30] hover:bg-red-50 transition-colors duration-150">
      <X size={12} weight="bold" /> Reject
    </button>
  </div>
);

const sortPending = (a, b) => (a.status === "pending" ? -1 : 1) - (b.status === "pending" ? -1 : 1);

export default function Approvals() {
  const [tab, setTab] = useState("leaves");
  const [leaves, setLeaves] = useState([]);
  const [expenses, setExpenses] = useState([]);

  const load = () => {
    api.get("/proxy/leaves").then((r) => setLeaves([...r.data].sort(sortPending))).catch(() => {});
    api.get("/proxy/expenses").then((r) => setExpenses([...r.data].sort(sortPending))).catch(() => {});
  };
  useEffect(load, []);

  const decide = (kind, id) => async (status) => {
    try {
      await api.patch(`/proxy/${kind}/${id}`, { status, decision_note: `${status} from admin console` });
      toast.success(`Request ${status}`);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Action failed");
    }
  };

  const pendingL = leaves.filter((l) => l.status === "pending").length;
  const pendingE = expenses.filter((e) => e.status === "pending").length;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="approvals-page">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Requests</p>
      <h1 className="font-heading font-black tracking-tight text-3xl mb-8">Approvals</h1>

      <div className="flex gap-0 mb-6 border border-slate-200 rounded-md w-fit overflow-hidden">
        <button data-testid="tab-leaves" onClick={() => setTab("leaves")} className={`px-5 py-2 text-sm font-semibold transition-colors duration-150 ${tab === "leaves" ? "bg-slate-950 text-white" : "hover:bg-slate-50"}`}>
          Leaves {pendingL > 0 && <span className="ml-1 text-[#FFCC00]">({pendingL})</span>}
        </button>
        <button data-testid="tab-expenses" onClick={() => setTab("expenses")} className={`px-5 py-2 text-sm font-semibold border-l border-slate-200 transition-colors duration-150 ${tab === "expenses" ? "bg-slate-950 text-white" : "hover:bg-slate-50"}`}>
          Expenses {pendingE > 0 && <span className="ml-1 text-[#FFCC00]">({pendingE})</span>}
        </button>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden max-w-full">
        {tab === "leaves" ? (
          <table className="w-full text-sm" data-testid="leaves-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                {["Employee", "Type", "From", "To", "Reason", "Status", ""].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leaves.map((l) => (
                <tr key={l.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium">{l.employee_name}</td>
                  <td className="px-4 py-3 capitalize text-slate-600">{l.leave_type}</td>
                  <td className="px-4 py-3 font-mono-data text-xs">{l.from_date}</td>
                  <td className="px-4 py-3 font-mono-data text-xs">{l.to_date}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs max-w-[240px] truncate">{l.reason}</td>
                  <td className="px-4 py-3"><Badge status={l.status} /></td>
                  <td className="px-4 py-3">{l.status === "pending" && <Actions onDecide={decide("leaves", l.id)} id={l.id} kind="leave" />}</td>
                </tr>
              ))}
              {leaves.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No leave requests.</td></tr>}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                {["Employee", "Category", "Amount", "Spent On", "Notes", "Status", ""].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {expenses.map((ex) => (
                <tr key={ex.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium">{ex.employee_name}</td>
                  <td className="px-4 py-3 capitalize text-slate-600">{ex.category}</td>
                  <td className="px-4 py-3 font-mono-data font-semibold">₹{ex.amount}</td>
                  <td className="px-4 py-3 font-mono-data text-xs">{ex.spent_on}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs max-w-[240px] truncate">{ex.notes || "—"}</td>
                  <td className="px-4 py-3"><Badge status={ex.status} /></td>
                  <td className="px-4 py-3">{ex.status === "pending" && <Actions onDecide={decide("expenses", ex.id)} id={ex.id} kind="expense" />}</td>
                </tr>
              ))}
              {expenses.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No expense claims.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
